/**
 * Lösungs-Landingpages (/loesungen/[slug]).
 *
 * Einstieg über das konkrete Bedürfnis: "Ich brauche eine Mitarbeiter-App / ein
 * Kundenportal / eine Terminbuchung – wer baut mir das?"
 * Das sind die Suchanfragen mit der höchsten Kaufabsicht, weil der Suchende
 * bereits weiß, was er will, und nur noch den Anbieter sucht.
 */

import type { LandingContent } from "./landing";

export const solutions: LandingContent[] = [
  // ────────────────────────────────────────────────────────────────
  {
    slug: "mitarbeiter-app",
    icon: "users",
    title: "Mitarbeiter-App",
    navTitle: "Mitarbeiter-App",
    tagline: "Ein Kanal für alle – auch für die ohne Firmen-E-Mail.",
    summary:
      "Eine App für Ihr Team: Dienstpläne, Infos, Dokumente und Meldungen erreichen auch die Mitarbeiter, die nie am Schreibtisch sitzen.",
    keywords: [
      "Mitarbeiter App entwickeln lassen",
      "Mitarbeiterplattform Unternehmen",
      "interne Kommunikation App KMU",
      "Dienstplan App Mitarbeiter",
      "Employee App Österreich",
    ],
    metaTitle: "Mitarbeiter-App entwickeln lassen",
    metaDescription:
      "Dienstplan, Infos und Dokumente erreichen alle – auch ohne Firmen-E-Mail. Mit Lesebestätigung. Jetzt kostenloses Erstgespräch sichern.",
    heroEyebrow: "Lösung",
    heroHeadline: "Eine App, die auch die erreicht, die nie am Schreibtisch sitzen",
    heroSub:
      "Aushang am schwarzen Brett, WhatsApp-Gruppen, Zettel im Spind: So erreichen Sie einen Teil Ihres Teams nie zuverlässig. Wir bauen Ihnen einen Kanal, den wirklich alle nutzen.",
    pains: [
      {
        title: "Die Hälfte des Teams hat keine Firmen-E-Mail",
        text: "Monteure, Fahrer, Service- und Produktionsmitarbeiter lesen keine Intranet-Mails. Wichtige Infos erreichen sie über Umwege oder gar nicht.",
      },
      {
        title: "WhatsApp-Gruppen sind ein Datenschutzproblem",
        text: "Private Nummern in Gruppen, Dienstpläne im selben Chat wie Urlaubsfotos, und beim Austritt bleibt alles dort. Praktisch – aber rechtlich heikel und unstrukturiert.",
      },
      {
        title: "Niemand weiß, wer was gelesen hat",
        text: "Sicherheitsunterweisung, neue Arbeitsanweisung, geänderte Zeiten: Sie hängen es aus und hoffen. Nachweisen können Sie es nicht.",
      },
      {
        title: "Immer dieselben Fragen an die Personalabteilung",
        text: "Wie viel Urlaub habe ich noch? Wo finde ich meinen Lohnzettel? Wie melde ich mich krank? Jede Frage einzeln beantwortet, jedes Mal aufs Neue.",
      },
    ],
    solutions: [
      {
        title: "Zugang ohne Firmen-E-Mail",
        text: "Anmeldung per Handynummer oder Personalnummer. Wer kein Firmenkonto hat, kommt trotzdem rein – das ist die Grundvoraussetzung, damit eine solche App überhaupt funktioniert.",
      },
      {
        title: "Lesebestätigung, wo sie zählt",
        text: "Bei Unterweisungen und wichtigen Anweisungen sehen Sie, wer bestätigt hat und wer nicht. Erinnerung geht automatisch raus, der Nachweis ist dokumentiert.",
      },
      {
        title: "Selbstbedienung statt Rückfragen",
        text: "Urlaubsstand, Lohnzettel, Krankmeldung, Dokumente: Das Team findet es selbst, rund um die Uhr. Die Personalabteilung bearbeitet nur noch, was wirklich Entscheidung braucht.",
      },
    ],
    sections: [
      {
        heading: "Warum die meisten internen Apps nach drei Monaten leer sind",
        body:
          "Der häufigste Grund ist nicht die Technik, sondern das Konzept: Die App bietet nichts, was der Mitarbeiter selbst braucht. Wenn ein Werkzeug nur dazu dient, dass das Unternehmen Nachrichten hineinschickt, wird es nach der Neugier-Phase ignoriert. Erfolgreiche Mitarbeiter-Apps drehen das um. Sie starten mit dem, was der Mitarbeiter ohnehin ständig wissen will: Wann arbeite ich nächste Woche? Wie viel Urlaub habe ich noch? Wo ist mein Lohnzettel? Wenn diese Dinge nur dort zu finden sind und dort auch wirklich funktionieren, wird die App täglich geöffnet. Erst dann funktionieren auch Mitteilungen von oben nach unten, weil ein Kanal existiert, den die Leute tatsächlich benutzen.",
      },
      {
        heading: "WhatsApp-Gruppen: bequem, aber ein reales Risiko",
        body:
          "Fast jeder Betrieb organisiert Teile der internen Kommunikation über WhatsApp, weil es sofort funktioniert und alle es können. Die Probleme kommen später: Private Telefonnummern werden zwischen Mitarbeitern geteilt, ohne dass jemand zugestimmt hat. Dienstliche Inhalte liegen auf privaten Geräten in einem Dienst, über den Sie keine Kontrolle haben. Beim Austritt eines Mitarbeiters bleiben Chatverläufe und Kontakte bestehen. Und im Streitfall gibt es keinen verwertbaren Nachweis, wer welche Anweisung wann erhalten hat. Eine eigene App löst all das, ohne dass die Bedienung komplizierter wird – die Hürde ist nicht die Technik, sondern die Umgewöhnung in den ersten Wochen.",
      },
      {
        heading: "Was so eine App enthalten sollte – und was nicht",
        body:
          "Der häufigste Fehler beim Start ist, zu viel zu wollen. Eine Mitarbeiter-App, die zum Projektstart fünfzehn Funktionen hat, wird nie fertig und überfordert die Nutzer. Bewährt hat sich ein Kern aus vier bis fünf Dingen: Dienstplan beziehungsweise Einsatzübersicht, Nachrichten und Aushänge mit Lesebestätigung, persönliche Dokumente wie Lohnzettel, Abwesenheitsanträge und eine Suche nach Ansprechpartnern. Das deckt den Alltag ab. Alles Weitere – Schulungen, Ideenmanagement, Feedbackbögen, Schichttausch – kommt später und nur, wenn der Kern tatsächlich genutzt wird. Diese Reihenfolge ist wichtiger, als sie klingt.",
      },
    ],
    visual: {
      kind: "matrix",
      caption: "Was draußen auf dem Handy gebraucht wird – und was nicht",
      good: {
        title: "Gehört in die App",
        items: [
          "Aufträge des Tages mit Adresse und Ansprechpartner",
          "Zeiten erfassen, ohne Zettel",
          "Fotos direkt zum Auftrag",
          "Material und Verbrauch festhalten",
          "Unterschrift des Kunden vor Ort",
        ],
      },
      bad: {
        title: "Bleibt im Büro",
        items: [
          "Auswertungen und Statistiken",
          "Stammdatenpflege",
          "Angebots- und Rechnungslegung",
          "Personalverwaltung",
        ],
      },
      note: "Eine App, die alles kann, wird draußen nicht benutzt. Entscheidend ist, was mit Handschuhen und schlechtem Empfang noch funktioniert.",
    },
    deliverables: [
      "Anmeldung ohne Firmen-E-Mail (Handynummer oder Personalnummer)",
      "Dienstplan- & Einsatzübersicht für jeden Mitarbeiter",
      "Nachrichten & Aushänge mit Lesebestätigung",
      "Persönliche Dokumente (Lohnzettel, Verträge, Nachweise)",
      "Urlaubsantrag & Krankmeldung direkt aus der App",
    ],
    example: {
      title: "Der Aushang, den wirklich alle gesehen haben",
      persona: "Typisches Szenario: Produktionsbetrieb mit 60 Mitarbeitern",
      problem:
        "Ein Großteil der Belegschaft hatte keine Firmen-E-Mail. Infos hingen am schwarzen Brett, Dienstpläne kursierten als Foto in WhatsApp-Gruppen, Unterweisungen waren nicht nachweisbar.",
      solution:
        "Eine App mit Anmeldung per Personalnummer, Dienstplan, persönlichen Dokumenten und Mitteilungen mit Lesebestätigung.",
      result:
        "Wichtige Mitteilungen erreichen nachweislich alle. Die Personalabteilung beantwortet deutlich weniger Standardfragen, weil das Team selbst nachsieht.",
    },
    faq: [
      {
        q: "Müssen die Mitarbeiter dafür ein privates Handy nutzen?",
        a: "Nicht zwingend. Die App läuft im Browser genauso wie als installierte App, und Sie können Terminals oder Tablets an Standorten aufstellen. Wo private Geräte genutzt werden, sollten Sie das mit dem Betriebsrat und arbeitsrechtlich sauber regeln – wir bauen die Technik so, dass beide Wege möglich sind.",
      },
      {
        q: "Wie aufwendig ist die Einführung im Team?",
        a: "Der technische Teil ist der kleinere. Entscheidend ist der Start: Wir empfehlen, mit einer Funktion zu beginnen, die für die Mitarbeiter sofort einen Vorteil hat – meist der Dienstplan. Wenn der nur noch dort zu finden ist, ist die App nach zwei Wochen etabliert.",
      },
      {
        q: "Kann die App mehrere Sprachen?",
        a: "Ja, und in Betrieben mit internationalem Team ist das meist entscheidend. Inhalte lassen sich mehrsprachig pflegen, und für Mitteilungen können wir eine KI-gestützte Übersetzung einbauen, damit nicht jeder Aushang manuell übersetzt werden muss.",
      },
    ],
    relatedServices: ["softwareentwicklung", "it-consulting"],
    relatedSlugs: ["handwerk", "transport-logistik", "zeiterfassung"],
  },

  // ────────────────────────────────────────────────────────────────
  {
    slug: "kundenportal",
    icon: "dashboard",
    title: "Kundenportal",
    navTitle: "Kundenportal",
    tagline: "Ihre Kunden sehen selbst nach, statt anzurufen.",
    summary:
      "Ein geschützter Bereich, in dem Kunden Unterlagen, Projektstände, Rechnungen und Termine jederzeit selbst einsehen – ohne Rückfragen bei Ihnen.",
    keywords: [
      "Kundenportal entwickeln lassen",
      "Kundenbereich Website erstellen",
      "sicherer Dokumentenaustausch Kunden",
      "Self-Service Portal KMU",
      "Kundenportal Software Österreich",
    ],
    metaTitle: "Kundenportal entwickeln lassen",
    metaDescription:
      "Dokumente, Projektstatus und Rechnungen jederzeit einsehbar – Ihre Kunden fragen nicht mehr nach. Jetzt kostenloses Erstgespräch sichern.",
    heroEyebrow: "Lösung",
    heroHeadline: "Jede Statusfrage, die Sie nicht beantworten müssen, ist gewonnene Zeit",
    heroSub:
      "„Wie weit ist das?\", „Können Sie mir die Rechnung nochmal schicken?\", „Wo finde ich den Vertrag?\" – ein Kundenportal beantwortet das rund um die Uhr, ohne dass jemand bei Ihnen etwas tun muss.",
    pains: [
      {
        title: "Dieselben Fragen, jeden Tag",
        text: "Statusanfragen, verlorene Rechnungen, gesuchte Unterlagen. Jede einzelne ist schnell beantwortet – in Summe ist es ein halber Arbeitstag pro Woche.",
      },
      {
        title: "Dokumente wandern per E-Mail hin und her",
        text: "Anhänge in Postfächern, weitergeleitet, mehrfach versendet, nach drei Monaten nicht mehr auffindbar. Bei sensiblen Unterlagen ist das zusätzlich ein Risiko.",
      },
      {
        title: "Der Kunde weiß nie, woran er ist",
        text: "Sie arbeiten seit zwei Wochen an dem Projekt, aber der Kunde sieht nichts davon. Kein Fortschritt sichtbar heißt für ihn: Es passiert nichts.",
      },
      {
        title: "Große Dateien passen nicht in E-Mails",
        text: "Pläne, Fotos, Videos – und dann kommt die Fehlermeldung. Also wieder ein Filehoster, wieder ein Link, wieder keine Kontrolle darüber, wer das öffnet.",
      },
    ],
    solutions: [
      {
        title: "Ein Ort für alles",
        text: "Verträge, Angebote, Rechnungen, Pläne und Protokolle liegen beim Kunden im Portal. Er findet sie selbst – heute und in zwei Jahren noch.",
      },
      {
        title: "Sichtbarer Fortschritt",
        text: "Meilensteine mit Status: erledigt, in Arbeit, offen. Der Kunde sieht, dass etwas passiert, ohne dass Sie einen Statusbericht schreiben.",
      },
      {
        title: "Sicher statt bequem-riskant",
        text: "Anmeldung mit Zwei-Faktor, Zugriffe protokolliert, Daten auf Servern in der EU. Keine unverschlüsselten Anhänge mehr, keine öffentlichen Filehoster-Links.",
      },
    ],
    sections: [
      {
        heading: "Was ein Kundenportal in der Praxis einspart",
        body:
          "Der offensichtliche Effekt sind weniger Anrufe und E-Mails. Der größere ist ein anderer: Jede Statusanfrage unterbricht jemanden mitten in der Arbeit, und diese Unterbrechung kostet weit mehr als die zwei Minuten für die Antwort selbst. Wer aus einer konzentrierten Tätigkeit gerissen wird, braucht spürbar Zeit, um wieder hineinzufinden. Wenn ein Portal einen erheblichen Teil dieser Unterbrechungen abfängt, gewinnt das Team nicht nur die Antwortzeit zurück, sondern auch die Konzentration. In kleineren Betrieben ist das oft der Punkt, an dem sich ein Portal am schnellsten rechnet – nicht in eingesparter Antwortzeit, sondern in wiedergewonnener Arbeitsruhe.",
      },
      {
        heading: "Transparenz schafft Vertrauen – und verhindert Streit",
        body:
          "Die meisten Konflikte in Dienstleistungsprojekten entstehen nicht durch schlechte Arbeit, sondern durch unterschiedliche Vorstellungen davon, was vereinbart war und wie weit es ist. Wenn beide Seiten jederzeit denselben Stand sehen – welche Meilensteine erledigt sind, welche Dokumente freigegeben wurden, wann was geliefert wurde –, verschwindet ein großer Teil dieser Reibung. Ein Portal ist damit nicht nur ein Servicewerkzeug, sondern auch ein Absicherungsinstrument: Freigaben und Übergaben sind mit Zeitstempel dokumentiert. Das hilft im Zweifelsfall beiden Seiten und macht Gespräche über Nachträge deutlich sachlicher.",
      },
      {
        heading: "Sicherheit ist bei einem Portal keine Option",
        body:
          "Ein Kundenportal enthält per Definition Daten, die nicht öffentlich sein dürfen. Deshalb gehört Sicherheit von Anfang an eingebaut und nicht nachgerüstet: Anmeldung mit zweitem Faktor, konsequente Trennung der Mandanten, sodass ein Kunde technisch keine fremden Daten erreichen kann, protokollierte Zugriffe, verschlüsselte Übertragung und Speicherung, Server innerhalb der EU sowie klare Löschfristen. Dazu kommt ein Berechtigungskonzept: Nicht jeder Ansprechpartner beim Kunden darf alles sehen. Diese Punkte klingen selbstverständlich, sind aber genau die Stellen, an denen fertige Baukastenlösungen häufig zu grob sind.",
      },
    ],
    visual: {
      kind: "compare",
      caption: "Unterlagen per E-Mail oder im Portal",
      columns: ["Per E-Mail", "Im Kundenportal"],
      rows: [
        { criterion: "Kunde findet den aktuellen Stand selbst", a: "no", b: "yes" },
        { criterion: "Nachvollziehbar, wer was wann gesehen hat", a: "no", b: "yes" },
        { criterion: "Große Dateien ohne Umweg", a: "no", b: "yes" },
        { criterion: "Keine Rückfrage „haben Sie das noch?“", a: "no", b: "yes" },
        { criterion: "Zugriff lässt sich wieder entziehen", a: "no", b: "yes" },
        { criterion: "Ohne Einrichtung sofort nutzbar", a: "yes", b: "part" },
        { criterion: "Kunde muss nichts Neues lernen", a: "yes", b: "part" },
      ],
      note: "Die letzten beiden Zeilen sind der Preis. Sie fallen ab dem Moment nicht mehr ins Gewicht, in dem die erste Rückfrage ausbleibt.",
    },
    deliverables: [
      "Geschützter Kundenbereich in Ihrem Design",
      "Dokumentenablage mit Versionen & Freigaben",
      "Projektstatus mit Meilensteinen",
      "Rechnungen & Angebote zum Selbst-Abrufen",
      "Zwei-Faktor-Anmeldung & Zugriffsprotokoll",
    ],
    example: {
      title: "Der halbe Tag, der wieder frei wurde",
      persona: "Typisches Szenario: Planungsbüro mit 20 Mitarbeitern",
      problem:
        "Pläne und Protokolle gingen per E-Mail und Filehoster raus. Kunden fragten ständig nach dem Stand und nach Dokumenten, die sie nicht mehr fanden.",
      solution:
        "Ein Kundenportal mit Dokumentenablage samt Versionen, sichtbaren Projektmeilensteinen und Zwei-Faktor-Anmeldung.",
      result:
        "Statusanfragen gehen deutlich zurück. Dokumente sind auffindbar statt in Postfächern verstreut, und Freigaben sind mit Zeitstempel nachvollziehbar.",
    },
    faq: [
      {
        q: "Nutzen unsere Kunden so ein Portal wirklich?",
        a: "Nur wenn es einfacher ist als nachzufragen. Deshalb bauen wir es bewusst schlank: Anmeldung ohne Hürden, alles Wichtige auf der Startseite, funktioniert am Handy. Und wichtig – wir hinterlegen die Dokumente nur dort, nicht zusätzlich per E-Mail. Solange beides parallel läuft, gewöhnt sich niemand um.",
      },
      {
        q: "Können wir das an unsere bestehenden Systeme anbinden?",
        a: "In der Regel ja. Rechnungen aus der Buchhaltung, Projektdaten aus Ihrem Projektwerkzeug, Kontakte aus dem CRM – wo Schnittstellen existieren, nutzen wir sie. Wo nicht, arbeiten wir mit regelmäßigen Importen. Was in Ihrem Fall geht, klären wir vorab konkret.",
      },
      {
        q: "Was passiert mit den Daten, wenn ein Kunde geht?",
        a: "Sie legen Löschfristen fest, und das System setzt sie automatisch durch. Der Kunde kann seine Daten vorher exportieren. Das ist nicht nur DSGVO-Anforderung, sondern erspart Ihnen auch das manuelle Aufräumen.",
      },
    ],
    relatedServices: ["softwareentwicklung", "qualitaetssicherung-qa"],
    relatedSlugs: ["steuerberater-kanzleien", "immobilien-hausverwaltung", "dokumenten-automatisierung"],
  },

  // ────────────────────────────────────────────────────────────────
  {
    slug: "terminbuchungssystem",
    icon: "calendar",
    title: "Terminbuchungssystem",
    navTitle: "Terminbuchung",
    tagline: "Rund um die Uhr buchbar – nach Ihren Regeln, ohne Provision.",
    summary:
      "Ein Buchungssystem auf Ihrer eigenen Website: mit Ihren Öffnungszeiten, Ihren Terminarten und Ihren Puffern. Automatische Erinnerungen senken Ausfälle.",
    keywords: [
      "Terminbuchungssystem erstellen lassen",
      "Online Terminvergabe Website",
      "Buchungssystem ohne Provision",
      "Terminerinnerung automatisch",
      "Terminsoftware Österreich",
    ],
    metaTitle: "Terminbuchungssystem ohne Provision",
    metaDescription:
      "Rund um die Uhr buchbar, nach Ihren Regeln. Automatische Erinnerungen senken Ausfälle spürbar. Jetzt kostenloses Erstgespräch sichern.",
    heroEyebrow: "Lösung",
    heroHeadline: "Termine kommen rein, während Sie arbeiten oder schlafen",
    heroSub:
      "Ein erheblicher Teil der Terminanfragen entsteht dann, wenn bei Ihnen niemand ans Telefon geht. Ein eigenes Buchungssystem fängt genau diese Anfragen ab – ohne Provision pro Termin.",
    pains: [
      {
        title: "Terminvergabe frisst Arbeitszeit",
        text: "Anrufen, nachsehen, Alternative vorschlagen, eintragen, bestätigen. Pro Termin einige Minuten – bei zwanzig Terminen am Tag ist das eine eigene Teilzeitstelle.",
      },
      {
        title: "Anfragen kommen zur Unzeit",
        text: "Abends, am Wochenende, in der Mittagspause. Wer dann nur eine Telefonnummer findet, verschiebt den Anruf – und vergisst ihn meistens.",
      },
      {
        title: "Nicht erschienene Kunden kosten direkt Geld",
        text: "Eine reservierte Stunde ohne Kunden lässt sich nicht nachholen. Bei mehreren pro Woche summiert sich das über das Jahr erheblich.",
      },
      {
        title: "Buchungsportale nehmen pro Termin mit",
        text: "Die Plattformen bringen Kunden und behalten einen Anteil oder eine Monatspauschale. Der Kundenkontakt bleibt bei ihnen, nicht bei Ihnen.",
      },
    ],
    solutions: [
      {
        title: "Ihre Regeln, nicht die eines Baukastens",
        text: "Terminarten mit eigener Dauer, Puffer zwischen Terminen, Mindestvorlauf, Sperrzeiten, Mitarbeiterkalender, Ausnahmen an Feiertagen. Das System lässt nur zu, was Sie erlaubt haben.",
      },
      {
        title: "Erinnerungen, die Ausfälle senken",
        text: "Automatisch per E-Mail oder SMS, mit Absage-Link. Eine Absage einen Tag vorher ist nachbesetzbar – ein Nichterscheinen ist verloren.",
      },
      {
        title: "Warteliste schließt Lücken automatisch",
        text: "Wird ein Termin frei, werden Wartende automatisch informiert. Der erste, der zusagt, bekommt den Platz – ohne dass jemand telefonieren muss.",
      },
    ],
    sections: [
      {
        heading: "Warum Baukasten-Buchungen oft mehr Arbeit machen",
        body:
          "Fertige Buchungswerkzeuge sind schnell eingerichtet und für einfache Fälle völlig ausreichend. Sobald die Realität komplexer wird, stoßen sie an Grenzen: unterschiedliche Dauer je Leistung, Ressourcen die gleichzeitig frei sein müssen – Raum, Gerät und Person –, unterschiedliche Vorlaufzeiten, Termine die nur bestimmte Mitarbeiter übernehmen dürfen, Serientermine, Anzahlungen. Bildet das System diese Regeln nicht ab, entstehen Buchungen, die Sie händisch korrigieren müssen. Dann haben Sie die Telefonarbeit nicht abgeschafft, sondern nur verlagert. Die Faustregel: Solange Ihre Terminlogik in drei Sätzen erklärt ist, nehmen Sie ein Standardwerkzeug. Sobald es komplizierter wird, lohnt sich eine eigene Lösung schnell.",
      },
      {
        heading: "Wie Erinnerungen Terminausfälle senken",
        body:
          "Die meisten Kunden und Patienten erscheinen nicht deshalb nicht, weil sie den Termin bewusst sausen lassen, sondern weil sie ihn vergessen haben – besonders bei Terminen, die Wochen vorher vereinbart wurden. Eine Erinnerung ein bis zwei Tage vorher wirkt deshalb stark. Entscheidend ist, dass sie eine einfache Absagemöglichkeit enthält. Das wirkt zunächst kontraproduktiv, ist aber der Kern der Sache: Eine Absage 24 Stunden vorher gibt Ihnen die Chance, den Platz nachzubesetzen. Kombiniert mit einer Warteliste schließt sich die Lücke oft von selbst, ohne dass jemand eingreifen muss.",
      },
      {
        heading: "Der eigene Kanal gegen die Provision der Portale",
        body:
          "Buchungsportale sind ein sinnvoller Kanal für Kunden, die Sie noch nicht kennen – dafür sind sie gebaut, und dafür ist die Provision auch gerechtfertigt. Unwirtschaftlich wird es, wenn Stammkunden über das Portal buchen: Für einen Kunden, der ohnehin zu Ihnen kommt, zahlen Sie dauerhaft eine Vermittlungsgebühr. Das Ziel ist deshalb nicht, die Portale abzuschaffen, sondern die eigene Buchung so sichtbar und bequem zu machen, dass Wiederkehrer direkt buchen. Praktisch heißt das: Buchungsknopf prominent auf der Website, Link in jeder Bestätigungsmail, QR-Code am Empfang und in der Signatur. Der Effekt zeigt sich über Monate, nicht über Nacht – dafür dauerhaft.",
      },
    ],
    visual: {
      kind: "compare",
      caption: "Telefonische Terminvergabe und Online-Buchung",
      columns: ["Nur Telefon", "Online-Buchung"],
      rows: [
        { criterion: "Termine entstehen auch nach Feierabend", a: "no", b: "yes" },
        { criterion: "Keine Unterbrechung der laufenden Arbeit", a: "no", b: "yes" },
        { criterion: "Erinnerung geht automatisch raus", a: "no", b: "yes" },
        { criterion: "Doppelbuchungen ausgeschlossen", a: "no", b: "yes" },
        { criterion: "Keine Provision je Termin", a: "yes", b: "yes" },
        { criterion: "Persönliche Rückfrage im Gespräch möglich", a: "yes", b: "part" },
      ],
      note: "Keine Provision gilt nur für ein eigenes System. Portale verlangen je nach Anbieter einen Anteil pro vermitteltem Termin.",
    },
    deliverables: [
      "Buchungssystem auf Ihrer Website, in Ihrem Design",
      "Terminarten, Puffer, Vorlauf & Sperrzeiten frei konfigurierbar",
      "Mitarbeiter- & Ressourcenkalender",
      "Automatische Bestätigung & Erinnerung (E-Mail/SMS)",
      "Warteliste zum Nachbesetzen freier Termine",
    ],
    example: {
      title: "Gebucht um 22:40 Uhr",
      persona: "Typisches Szenario: Praxis mit drei Behandlungsräumen",
      problem:
        "Termine wurden ausschließlich telefonisch vergeben. Die Rezeption war vormittags durchgehend am Telefon, und vergessene Termine rissen Lücken in den Plan.",
      solution:
        "Ein Buchungssystem mit hinterlegten Terminarten und Puffern, automatischer Erinnerung zwei Tage vorher inklusive Absage-Link und einer Warteliste.",
      result:
        "Ein großer Teil der Buchungen entsteht außerhalb der Öffnungszeiten. Freiwerdende Termine werden über die Warteliste nachbesetzt statt zu verfallen.",
    },
    faq: [
      {
        q: "Kann ich mein bestehendes Kalendersystem weiter nutzen?",
        a: "In vielen Fällen ja. Google Kalender, Microsoft 365 und andere lassen sich anbinden, sodass Buchungen dort erscheinen und Ihre privaten Termine automatisch blockieren. Was konkret geht, hängt vom System ab – das klären wir vor dem Angebot.",
      },
      {
        q: "Was, wenn jemand einen unpassenden Termin bucht?",
        a: "Das verhindern die Regeln. Sie legen fest, welche Terminart wie lange dauert, wie viel Vorlauf nötig ist, welche Zeiten überhaupt buchbar sind und welche Termine grundsätzlich nur nach Rücksprache vergeben werden. Buchbar ist nur, was Sie freigegeben haben.",
      },
      {
        q: "Lassen sich Anzahlungen einbauen?",
        a: "Ja. Bei Terminen mit hoher Ausfallquote oder langer Dauer ist eine Anzahlung bei der Buchung wirksam. Wir binden gängige Zahlungsanbieter an – ob das für Ihre Kunden passt, sollten Sie allerdings gut abwägen, weil es die Buchungsschwelle erhöht.",
      },
    ],
    relatedServices: ["softwareentwicklung", "suchmaschinenoptimierung-seo"],
    relatedSlugs: ["arztpraxen-therapeuten", "friseur-beauty-studios", "gastronomie-hotellerie"],
  },

  // ────────────────────────────────────────────────────────────────
  {
    slug: "angebots-software",
    icon: "file-text",
    title: "Angebots- & Auftragssoftware",
    navTitle: "Angebotssoftware",
    tagline: "Vom Kundengespräch zum fertigen Angebot – in Minuten.",
    summary:
      "Angebote aus dem eigenen Leistungskatalog erstellen, versenden, nachverfolgen und mit einem Klick in Auftrag und Rechnung überführen.",
    keywords: [
      "Angebotssoftware erstellen lassen",
      "Angebote schneller schreiben Software",
      "Auftragsverwaltung KMU",
      "Kalkulationssoftware individuell",
      "Angebot Auftrag Rechnung Programm",
    ],
    metaTitle: "Angebotssoftware für Ihren Betrieb",
    metaDescription:
      "Vom Kundengespräch zum fertigen Angebot in Minuten, mit automatischem Nachfassen. Jetzt kostenloses Erstgespräch sichern.",
    heroEyebrow: "Lösung",
    heroHeadline: "Wer zuerst anbietet, bekommt den Auftrag",
    heroSub:
      "Zwischen Kundengespräch und fertigem Angebot liegen bei den meisten Betrieben Tage. In dieser Zeit entscheidet sich der Kunde oft für jemand anderen. Wir verkürzen diese Strecke auf Minuten.",
    pains: [
      {
        title: "Angebote entstehen abends nach Feierabend",
        text: "Tagsüber ist keine Zeit. Also sitzen Sie um neun Uhr abends vor Word und tippen zum vierten Mal in dieser Woche fast dasselbe.",
      },
      {
        title: "Jedes Angebot sieht anders aus",
        text: "Jeder im Team hat seine eigene Vorlage, seine eigenen Formulierungen und seine eigenen Preise im Kopf. Beim Kunden kommt Unordnung an.",
      },
      {
        title: "Niemand verfolgt nach",
        text: "Das Angebot ist raus – und dann passiert nichts mehr. Kein Erinnerung, kein Nachfassen. Aufträge gehen verloren, weil sich einfach niemand mehr gemeldet hat.",
      },
      {
        title: "Alles wird zweimal getippt",
        text: "Aus dem Angebot wird ein Auftrag, aus dem Auftrag eine Rechnung – und jedes Mal überträgt jemand dieselben Positionen von Hand. Jede Übertragung ist eine Fehlerquelle.",
      },
    ],
    solutions: [
      {
        title: "Katalog statt weißes Blatt",
        text: "Ihre Leistungen mit Texten, Preisen und Einheiten hinterlegt. Position auswählen, Menge eintragen, fertig. Kein Formulieren, kein Nachschlagen von Preisen.",
      },
      {
        title: "Nachfassen läuft automatisch",
        text: "Sie sehen, ob das Angebot geöffnet wurde, und das System erinnert Sie nach der von Ihnen gewählten Frist. Kein Angebot geht mehr verloren, weil es vergessen wurde.",
      },
      {
        title: "Ein Datensatz vom Angebot bis zur Rechnung",
        text: "Angenommenes Angebot wird per Klick zum Auftrag, erledigter Auftrag zur Rechnung. Die Positionen wandern mit – nichts wird zweimal getippt.",
      },
    ],
    sections: [
      {
        heading: "Warum Geschwindigkeit beim Angebot fast alles entscheidet",
        body:
          "Wer eine Leistung braucht, holt selten nur ein Angebot ein. Meist fragt er zwei bis vier Anbieter gleichzeitig an, und die Entscheidung fällt oft nicht nach dem besten Preis, sondern nach dem ersten brauchbaren Angebot – besonders bei kleineren Aufträgen und bei allem, was dringend ist. Ein Angebot, das noch am selben Tag beim Kunden liegt, hat eine deutlich bessere Ausgangsposition als eines, das nach einer Woche kommt, selbst wenn es teurer ist. Der Grund ist einfach: Schnelligkeit wird als Zuverlässigkeit gelesen. Wer schnell anbietet, so die Erwartung, arbeitet auch zügig. Deshalb ist die Zeit vom Gespräch bis zum Angebot einer der wirksamsten Hebel überhaupt.",
      },
      {
        heading: "Der eigene Leistungskatalog ist die halbe Arbeit",
        body:
          "Die meiste Zeit beim Angebotschreiben geht nicht in die Kalkulation, sondern ins Formulieren und Nachschlagen: Wie haben wir das letzte Mal beschrieben? Was war der Preis? Welcher Zuschlag gilt hier? Ein sauber gepflegter Leistungskatalog löst das dauerhaft – jede Position einmal ordentlich beschrieben, mit Einheit, Preis und optionalen Varianten. Danach besteht Angebotschreiben aus Auswählen statt Schreiben. Der Aufbau dieses Katalogs ist die eigentliche Arbeit beim Projektstart, und wir machen sie gemeinsam mit Ihnen. Der Nebeneffekt ist mindestens so wertvoll: Alle im Team bieten dieselben Leistungen zu denselben Preisen an, mit denselben Texten.",
      },
      {
        heading: "Nachfassen ist kein Verkaufstrick, sondern Handwerk",
        body:
          "Ein erheblicher Teil der Angebote, die ohne Antwort bleiben, werden nicht abgelehnt – sie gehen im Alltag des Kunden unter. Eine freundliche Nachfrage nach einer Woche ist deshalb kein Aufdringlichsein, sondern schlicht ordentliche Arbeit. Das Problem ist, dass sich niemand merkt, welches Angebot wann rausgegangen ist und bei welchem noch nichts zurückkam. Genau das übernimmt das System: Es zeigt offene Angebote nach Alter sortiert, erinnert automatisch und verrät Ihnen, ob der Kunde das Angebot überhaupt geöffnet hat. Das allein verändert die Abschlussquote spürbar, ohne dass Sie eine einzige zusätzliche Verkaufstechnik lernen müssen.",
      },
    ],
    visual: {
      kind: "matrix",
      caption: "Woran Angebote in der Praxis scheitern",
      good: {
        title: "Was ein System löst",
        items: [
          "Preise stehen an einer Stelle und sind überall aktuell",
          "Textbausteine statt Abschreiben vom letzten Angebot",
          "Jede Version bleibt nachvollziehbar",
          "Aus dem Angebot wird per Klick der Auftrag",
          "Nachfassen wird erinnert statt vergessen",
        ],
      },
      bad: {
        title: "Was es nicht löst",
        items: [
          "Einen Preis, der nicht kalkuliert ist",
          "Ein Angebot, das der Kunde nie angefragt hat",
          "Fehlende Entscheidung auf Kundenseite",
          "Zu späte Angebotslegung aus Zeitmangel",
        ],
      },
      note: "Die rechte Spalte ist wichtiger als die linke: Software beschleunigt, was man tut – sie ersetzt nicht, dass man es tut.",
    },
    deliverables: [
      "Leistungskatalog mit Texten, Preisen & Varianten",
      "Angebotserstellung in Minuten, in Ihrem Layout",
      "Versand, Öffnungs-Anzeige & automatisches Nachfassen",
      "Ein Klick vom Angebot zum Auftrag zur Rechnung",
      "Auswertung: Abschlussquote, offene Angebote, Umsatz je Leistung",
    ],
    example: {
      title: "Das Angebot, das noch beim Kunden fertig war",
      persona: "Typisches Szenario: Installationsbetrieb, 8 Mitarbeiter",
      problem:
        "Angebote entstanden abends in Word. Jeder im Team nutzte eine eigene Vorlage, Nachfassen passierte nie, und Positionen wurden für Auftrag und Rechnung erneut abgetippt.",
      solution:
        "Ein Leistungskatalog mit hinterlegten Preisen und Texten, Angebotserstellung am Tablet beim Kunden, automatisches Nachfassen und Überführung in Auftrag und Rechnung per Klick.",
      result:
        "Angebote gehen am selben Tag raus. Offene Angebote werden nachverfolgt statt vergessen, und Übertragungsfehler zwischen Angebot und Rechnung entfallen.",
    },
    faq: [
      {
        q: "Wir haben schon ein Buchhaltungsprogramm. Doppelt sich das?",
        a: "Nein, wenn wir es richtig abgrenzen. Die Buchhaltung bleibt, wo sie ist – wir decken die Strecke davor ab: Angebot, Nachfassen, Auftrag. Die fertige Rechnung übergeben wir per Schnittstelle oder Export an Ihr Buchhaltungsprogramm, statt sie dort nochmal zu erfassen.",
      },
      {
        q: "Können unterschiedliche Kunden unterschiedliche Preise haben?",
        a: "Ja. Kundenspezifische Preise, Rabattstaffeln, Rahmenverträge und Sonderkonditionen lassen sich hinterlegen und werden automatisch gezogen. Das ist einer der Punkte, an denen Standardsoftware oft nicht mitkommt.",
      },
      {
        q: "Wie kommen unsere bisherigen Leistungen und Preise ins System?",
        a: "Das machen wir gemeinsam beim Aufbau. Meist gibt es bereits Preislisten oder alte Angebote, aus denen sich der Katalog ableiten lässt. Diese Struktur einmal sauber zu erarbeiten ist der wesentliche Teil des Projekts – danach pflegen Sie sie selbst.",
      },
    ],
    relatedServices: ["softwareentwicklung", "kuenstliche-intelligenz"],
    relatedSlugs: ["handwerk", "excel-abloesen"],
  },

  // ────────────────────────────────────────────────────────────────
  {
    slug: "excel-abloesen",
    icon: "database",
    title: "Excel ablösen",
    navTitle: "Excel ablösen",
    tagline: "Wenn die Tabelle zum Risiko geworden ist.",
    summary:
      "Aus gewachsenen Excel-Dateien wird ein sauberes System: mehrbenutzerfähig, nachvollziehbar, ohne kaputte Formeln und ohne die Angst vor der einen falschen Zelle.",
    keywords: [
      "Excel durch Software ersetzen",
      "Excel Ablösung Unternehmen",
      "Datenbank statt Excel",
      "Excel Chaos beheben",
      "individuelle Software statt Tabelle",
    ],
    metaTitle: "Excel ablösen durch echte Software",
    metaDescription:
      "Alle arbeiten gleichzeitig, Fehleingaben werden verhindert, jede Änderung nachvollziehbar. Jetzt kostenloses Erstgespräch sichern.",
    heroEyebrow: "Lösung",
    heroHeadline: "Ihre wichtigste Datei hat 40 Spalten und niemand traut sich mehr ran",
    heroSub:
      "Excel war der richtige Start. Irgendwann wurde daraus das System, an dem der halbe Betrieb hängt – mit Formeln, die keiner mehr versteht, und einer Datei, die nur einer gleichzeitig öffnen kann.",
    pains: [
      {
        title: "Nur einer kann gleichzeitig arbeiten",
        text: "„Die Datei ist gesperrt.\" Also wird eine Kopie angelegt, parallel gearbeitet und später irgendwie zusammengeführt. Spätestens hier gehen Daten verloren.",
      },
      {
        title: "Eine falsche Zelle, und alles kippt",
        text: "Jemand sortiert eine Spalte ohne die anderen, überschreibt eine Formel oder fügt eine Zeile an der falschen Stelle ein. Der Fehler fällt Wochen später auf – und ist dann kaum noch zu finden.",
      },
      {
        title: "Niemand weiß, wer was geändert hat",
        text: "Eine Zahl stimmt nicht. Wer hat sie geändert, wann und warum? Es gibt keine Historie. Also raten alle, und niemand ist zuständig.",
      },
      {
        title: "Alles hängt an einer Person",
        text: "Die Datei hat jemand vor Jahren gebaut. Wenn diese Person kündigt oder krank wird, versteht niemand mehr, wie es funktioniert – und ändern traut sich erst recht keiner.",
      },
    ],
    solutions: [
      {
        title: "Alle gleichzeitig, ohne Konflikte",
        text: "Mehrere Personen arbeiten parallel am selben Datenbestand. Keine gesperrten Dateien, keine Kopien, keine Zusammenführung von Hand.",
      },
      {
        title: "Falsche Eingaben werden verhindert",
        text: "Pflichtfelder, Auswahllisten, Plausibilitätsprüfungen. Was fachlich unmöglich ist, lässt sich gar nicht erst eingeben – statt später mühsam korrigiert zu werden.",
      },
      {
        title: "Jede Änderung ist nachvollziehbar",
        text: "Wer hat wann was geändert, und wie war es vorher. Bei kritischen Daten ist das nicht nur praktisch, sondern oft eine Anforderung von Prüfern und Versicherungen.",
      },
    ],
    sections: [
      {
        heading: "Wann Excel noch richtig ist – und wann nicht mehr",
        body:
          "Excel ist ein hervorragendes Werkzeug, und wir raten regelmäßig davon ab, es abzulösen. Für einmalige Auswertungen, persönliche Notizen, schnelle Kalkulationen und alles, was eine Person allein bearbeitet, gibt es kaum etwas Besseres. Kritisch wird es an drei klar erkennbaren Punkten: wenn mehrere Personen gleichzeitig an denselben Daten arbeiten müssen, wenn die Datei geschäftskritisch geworden ist – also ihr Verlust den Betrieb ernsthaft stören würde –, und wenn niemand mehr versteht, wie sie funktioniert. Trifft einer dieser Punkte zu, ist die Tabelle vom Werkzeug zum Risiko geworden. Wenn keiner zutrifft, lassen Sie sie in Ruhe und investieren Sie das Geld woanders.",
      },
      {
        heading: "Wie eine Ablösung abläuft, ohne den Betrieb zu stören",
        body:
          "Der häufigste Fehler ist der große Wurf: alles auf einmal ersetzen, monatelang bauen, dann umstellen. Das geht regelmäßig schief, weil während der Bauzeit weitergearbeitet wird und sich die Anforderungen ändern. Wir gehen anders vor. Zuerst analysieren wir die bestehende Datei und schreiben auf, was sie fachlich eigentlich tut – das ist oft schon der wertvollste Teil, weil dabei Regeln sichtbar werden, die nur im Kopf einer Person existierten. Dann bauen wir den ersten, wichtigsten Ausschnitt und lassen ihn eine Zeit lang parallel zur Excel-Datei laufen. Erst wenn die Zahlen übereinstimmen und das Team dem neuen System traut, kommt der nächste Ausschnitt dazu.",
      },
      {
        heading: "Was Sie durch die Ablösung zusätzlich gewinnen",
        body:
          "Die offensichtlichen Vorteile sind Mehrbenutzerbetrieb und weniger Fehler. Der oft größere Gewinn liegt woanders: Sobald die Daten sauber strukturiert in einer Datenbank liegen, sind Auswertungen möglich, die in Excel praktisch niemand gebaut hätte. Umsatz je Kunde über drei Jahre, Auslastung je Mitarbeiter, welche Leistung tatsächlich Deckungsbeitrag bringt und welche nur beschäftigt. Dazu kommen Automatisierungen: Erinnerungen, wenn eine Frist naht, automatische Berichte, Anbindung an andere Systeme. Das alles ist auf einer Tabellendatei kaum sinnvoll umsetzbar – auf einer Datenbank ist es Routine.",
      },
    ],
    visual: {
      kind: "compare",
      caption: "Gewachsene Tabelle und eigenes System",
      columns: ["Excel-Datei", "Eigenes System"],
      rows: [
        { criterion: "Mehrere arbeiten gleichzeitig darin", a: "no", b: "yes" },
        { criterion: "Nachvollziehbar, wer was geändert hat", a: "no", b: "yes" },
        { criterion: "Eingaben werden auf Plausibilität geprüft", a: "no", b: "yes" },
        { criterion: "Rechte je Person steuerbar", a: "no", b: "yes" },
        { criterion: "Keine Kopie der Kopie im Umlauf", a: "no", b: "yes" },
        { criterion: "Sofort ohne Einführung nutzbar", a: "yes", b: "no" },
        { criterion: "Jeder kann es selbst anpassen", a: "yes", b: "part" },
      ],
      note: "Die letzten beiden Zeilen sind der Grund, warum die Tabelle entstanden ist – und warum sie so lange gehalten hat.",
    },
    deliverables: [
      "Analyse Ihrer bestehenden Dateien & der darin versteckten Regeln",
      "Mehrbenutzerfähiges System mit Rechteverwaltung",
      "Eingabeprüfung & Plausibilitätskontrollen",
      "Vollständige Änderungshistorie",
      "Auswertungen & automatische Berichte",
    ],
    example: {
      title: "Die Datei, die niemand mehr anfassen wollte",
      persona: "Typisches Szenario: Dienstleister mit 25 Mitarbeitern",
      problem:
        "Die zentrale Projektliste hatte über Jahre 40 Spalten und verschachtelte Formeln angesammelt. Nur einer konnte sie gleichzeitig öffnen, und der ursprüngliche Ersteller war längst nicht mehr im Unternehmen.",
      solution:
        "Analyse der enthaltenen Regeln, dann schrittweise Ablösung: zuerst die Projektübersicht, parallel zur Datei laufend, danach Zeiterfassung und Auswertungen.",
      result:
        "Das Team arbeitet gleichzeitig am selben Stand. Fehleingaben werden abgefangen, jede Änderung ist nachvollziehbar – und es gibt Auswertungen, die vorher niemand bauen konnte.",
    },
    faq: [
      {
        q: "Gehen unsere bestehenden Daten verloren?",
        a: "Nein. Die Übernahme der Altdaten ist fester Bestandteil des Projekts. Wir importieren den Bestand, prüfen ihn auf Dubletten und Unstimmigkeiten und gleichen die Ergebnisse mit Ihnen ab. Ihre Excel-Dateien behalten Sie zusätzlich als Archiv.",
      },
      {
        q: "Können wir weiterhin nach Excel exportieren?",
        a: "Ja, und das empfehlen wir ausdrücklich. Für Ad-hoc-Auswertungen, für den Steuerberater oder einfach, weil jemand lieber in Excel rechnet, ist der Export sinnvoll. Der Unterschied ist, dass Excel dann Auswertungswerkzeug ist statt Datenspeicher.",
      },
      {
        q: "Was, wenn wir gar nicht genau wissen, was unsere Datei alles macht?",
        a: "Das ist der Normalfall, kein Sonderfall. Genau deshalb beginnt jedes solche Projekt mit einer Analyse. Wir gehen die Datei mit Ihnen durch, machen die enthaltenen Regeln explizit und schreiben sie auf. Viele Kunden sagen im Nachhinein, dass allein dieser Schritt den Aufwand wert war.",
      },
    ],
    relatedServices: ["softwareentwicklung", "it-consulting", "qualitaetssicherung-qa"],
    relatedSlugs: ["handwerk", "einzelhandel-ecommerce", "angebots-software"],
  },

  // ────────────────────────────────────────────────────────────────
  {
    slug: "ki-chatbot-website",
    icon: "message",
    title: "KI-Chatbot für Ihre Website",
    navTitle: "KI-Chatbot",
    tagline: "Antwortet nachts, am Wochenende und im Urlaub.",
    summary:
      "Ein Chatbot, der Ihre Leistungen, Preise und Abläufe kennt, Standardfragen sofort beantwortet und bei komplexen Fällen sauber an einen Menschen übergibt.",
    keywords: [
      "KI Chatbot Website erstellen lassen",
      "Chatbot für Unternehmen Österreich",
      "KI Kundenservice Automatisierung",
      "Chatbot DSGVO konform",
      "Website Chatbot KMU",
    ],
    metaTitle: "KI-Chatbot für Ihre Website",
    metaDescription:
      "Beantwortet Standardfragen rund um die Uhr und übergibt sauber an einen Menschen. DSGVO-konform. Jetzt kostenloses Erstgespräch sichern.",
    heroEyebrow: "Lösung",
    heroHeadline: "Die immer gleichen Fragen beantwortet ab jetzt jemand anderes",
    heroSub:
      "Öffnungszeiten, Preise, Ablauf, Verfügbarkeit: Ein großer Teil Ihrer Anfragen dreht sich um eine Handvoll Themen. Ein KI-Chatbot, der Ihre Inhalte kennt, nimmt Ihnen genau diesen Teil ab – rund um die Uhr.",
    pains: [
      {
        title: "Dieselben zehn Fragen, immer wieder",
        text: "Was kostet das ungefähr? Wie lange dauert es? Kommen Sie auch nach Niederösterreich? Jede Antwort ist schnell – aber es sind Dutzende pro Woche.",
      },
      {
        title: "Anfragen kommen, wenn niemand da ist",
        text: "Abends und am Wochenende schaut sich jemand Ihre Seite an. Er findet die Antwort nicht, schreibt nicht – und ist am Montag bei jemand anderem.",
      },
      {
        title: "Unqualifizierte Anfragen kosten Zeit",
        text: "Sie telefonieren zwanzig Minuten, um festzustellen, dass Budget oder Region gar nicht passen. Diese Vorklärung könnte vorher passieren.",
      },
      {
        title: "Standard-Chatbots blamieren Sie",
        text: "Die einfachen Klick-Bots verstehen nichts und antworten daneben. Das wirkt schlechter als gar kein Chat und schadet dem Eindruck mehr, als es nützt.",
      },
    ],
    solutions: [
      {
        title: "Er kennt Ihre Inhalte, nicht das halbe Internet",
        text: "Der Bot antwortet auf Basis Ihrer Website, Ihrer Leistungsbeschreibungen und Ihrer FAQ. Was er nicht weiß, erfindet er nicht – dann übergibt er.",
      },
      {
        title: "Übergabe an einen Menschen, sauber",
        text: "Bei komplexen oder heiklen Fragen sammelt er die Eckdaten und leitet weiter – mit dem bisherigen Gesprächsverlauf, damit der Kunde nichts wiederholen muss.",
      },
      {
        title: "Anfragen kommen vorqualifiziert an",
        text: "Branche, Umfang, Zeitrahmen, Region: Der Bot fragt das im Gespräch ab. Sie steigen in ein Telefonat ein, bei dem die Grundlagen schon geklärt sind.",
      },
    ],
    sections: [
      {
        heading: "Der Unterschied zwischen Klick-Bot und KI-Chatbot",
        body:
          "Die Chatbots, die den meisten Menschen den Ruf der Technologie verdorben haben, sind regelbasiert: Sie folgen einem festen Entscheidungsbaum und verstehen nur die Formulierungen, die jemand vorher eingetragen hat. Wer anders fragt, landet in der Sackgasse. Ein KI-gestützter Chatbot arbeitet grundlegend anders – er versteht die Frage in natürlicher Sprache und formuliert die Antwort aus den Inhalten, die Sie ihm zur Verfügung stellen. Entscheidend dabei ist die Begrenzung auf Ihre eigenen Inhalte: Der Bot soll aus Ihren Leistungsbeschreibungen und FAQ antworten, nicht aus allgemeinem Weltwissen. Das verhindert erfundene Antworten, die im Kundenkontakt richtig teuer werden können.",
      },
      {
        heading: "Was ein Chatbot leisten kann – und wo die Grenze liegt",
        body:
          "Sinnvoll ist ein Chatbot bei allem, was oft und in ähnlicher Form gefragt wird: Leistungsumfang, grober Preisrahmen, Ablauf, Einzugsgebiet, Terminverfügbarkeit, Öffnungszeiten. Diese Fragen machen erfahrungsgemäß den größten Teil der Erstkontakte aus. Nicht sinnvoll ist er bei allem, was Beratung, Einschätzung oder Verhandlung erfordert – und bei allem, wo ein Fehler richtig weh tut: verbindliche Preiszusagen, rechtliche oder medizinische Auskünfte, Beschwerden. Deshalb bauen wir immer eine klare Grenze ein: Der Bot weiß, was er nicht beantworten darf, und übergibt dort ohne Umweg an einen Menschen. Ein Bot, der seine Grenzen kennt, wirkt kompetent. Einer, der alles versucht, wirkt unseriös.",
      },
      {
        heading: "Datenschutz beim Einsatz von KI im Kundenkontakt",
        body:
          "Sobald Besucher in einen Chat schreiben, entstehen personenbezogene Daten – oft mehr, als den Betreibern bewusst ist, weil Menschen in Chats erstaunlich freimütig sind. Sauber gelöst heißt: transparenter Hinweis, dass hier ein KI-System antwortet, klare Information darüber, wo die Verarbeitung stattfindet, Speicherung nur so lange wie nötig, keine Weitergabe an Dritte über das technisch Notwendige hinaus und Verarbeitung möglichst innerhalb der EU. Je nach Anwendungsfall und Sensibilität der Daten setzen wir auf europäische Anbieter oder auf Modelle, die in Ihrer eigenen Infrastruktur laufen. Was in Ihrem Fall angemessen ist, klären wir vor der Umsetzung und nicht danach.",
      },
    ],
    visual: {
      kind: "matrix",
      caption: "Wofür ein Chatbot taugt – und wofür nicht",
      good: {
        title: "Dafür taugt er",
        items: [
          "Dieselben Fragen zu Öffnungszeiten, Ablauf und Preisen",
          "Besucher zum passenden Formular führen",
          "Erste Angaben aufnehmen, bevor jemand zurückruft",
          "Auskunft außerhalb der Geschäftszeiten",
          "Auf Ihren eigenen Unterlagen antworten",
        ],
      },
      bad: {
        title: "Dafür nicht",
        items: [
          "Verbindliche Preis- oder Rechtsauskunft",
          "Beschwerden und heikle Fälle",
          "Fachberatung, die Rückfragen braucht",
          "Ersatz für eine erreichbare Telefonnummer",
        ],
      },
      note: "Ein Chatbot, der nicht weiterweiß, muss sauber an einen Menschen übergeben. Sonst kostet er die Anfrage, die er bringen sollte.",
    },
    deliverables: [
      "KI-Chatbot auf Basis Ihrer eigenen Inhalte",
      "Klare Grenzen: keine erfundenen Antworten",
      "Übergabe an einen Menschen inklusive Gesprächsverlauf",
      "Vorqualifizierung von Anfragen (Branche, Umfang, Region)",
      "Auswertung: Was fragen Ihre Besucher wirklich?",
    ],
    example: {
      title: "Sonntagabend, 21 Uhr, Anfrage qualifiziert",
      persona: "Typisches Szenario: Dienstleister mit hohem Anfragevolumen",
      problem:
        "Ein Großteil der Anfragen drehte sich um dieselben Themen: Preisrahmen, Ablauf und Einzugsgebiet. Anfragen außerhalb der Bürozeiten blieben unbeantwortet, bis jemand am nächsten Werktag Zeit hatte.",
      solution:
        "Ein KI-Chatbot auf Basis der Leistungsseiten und FAQ, der Standardfragen beantwortet, Eckdaten abfragt und bei allem Weiteren an einen Menschen übergibt.",
      result:
        "Standardfragen werden sofort beantwortet, auch nachts. Die Anfragen, die tatsächlich beim Team landen, sind vorgeklärt – das Erstgespräch startet auf einem anderen Niveau.",
    },
    faq: [
      {
        q: "Was, wenn der Bot etwas Falsches sagt?",
        a: "Deshalb begrenzen wir ihn auf Ihre eigenen Inhalte und definieren Themen, die er grundsätzlich nicht beantwortet, sondern weiterleitet. Zusätzlich sehen Sie alle Gesprächsverläufe und können nachschärfen. Eine hundertprozentige Garantie gibt es bei keiner KI – deshalb gehören verbindliche Zusagen niemals in den Bot.",
      },
      {
        q: "Merken Besucher, dass sie mit einer KI sprechen?",
        a: "Ja, und das soll so sein. Wir weisen transparent darauf hin. Verschleiern schadet dem Vertrauen und ist je nach Anwendungsfall auch rechtlich fragwürdig. Die meisten Menschen haben kein Problem mit einem Bot – solange er nützlich ist und sie jederzeit zu einem Menschen kommen.",
      },
      {
        q: "Wie viel Aufwand ist die Pflege?",
        a: "Wenig, weil der Bot direkt auf Ihre Website-Inhalte zugreift. Ändert sich eine Leistungsbeschreibung, ändert sich seine Antwort mit. Sinnvoll ist, monatlich in die Auswertung zu schauen: Fragen, die er nicht beantworten konnte, sind meist ein Hinweis auf eine Lücke auf Ihrer Website.",
      },
    ],
    relatedServices: ["kuenstliche-intelligenz", "softwareentwicklung", "suchmaschinenoptimierung-seo"],
    relatedSlugs: ["gastronomie-hotellerie", "einzelhandel-ecommerce", "kundenportal"],
  },

  // ────────────────────────────────────────────────────────────────
  {
    slug: "zeiterfassung",
    icon: "timer",
    title: "Zeiterfassung",
    navTitle: "Zeiterfassung",
    tagline: "Erfasst in Sekunden, abgerechnet ohne Rätselraten.",
    summary:
      "Mobile Zeiterfassung für Baustelle, Außendienst und Büro: rechtskonform, projektbezogen und ohne Zettel, die am Monatsende entziffert werden müssen.",
    keywords: [
      "Zeiterfassung App Unternehmen",
      "mobile Zeiterfassung Baustelle",
      "Arbeitszeiterfassung Pflicht Österreich",
      "Projektzeiterfassung Software",
      "digitale Stundenzettel",
    ],
    metaTitle: "Mobile Zeiterfassung für Ihr Team",
    metaDescription:
      "In zwei Taps erfasst, auch offline. Zuschläge automatisch, Export in die Lohnverrechnung. Jetzt kostenloses Erstgespräch sichern.",
    heroEyebrow: "Lösung",
    heroHeadline: "Stunden, die niemand notiert hat, werden auch nie bezahlt",
    heroSub:
      "Zettel im Auto, Notizen im Handy, Erinnerung am Monatsende: So gehen jeden Monat verrechenbare Stunden verloren. Wir bauen eine Erfassung, die in zwei Taps erledigt ist.",
    pains: [
      {
        title: "Stundenzettel kommen zu spät und unleserlich",
        text: "Am Monatsende sammeln Sie Zettel ein, entziffern Handschriften und rufen nach, was fehlt. Das kostet jedes Mal Tage.",
      },
      {
        title: "Verrechenbare Stunden gehen verloren",
        text: "Die halbe Stunde Fahrt, der kurze Zusatzhandgriff, der Materialweg: Was nicht sofort erfasst wird, ist am Abend vergessen – und landet nie auf einer Rechnung.",
      },
      {
        title: "Niemand weiß, wie ein Projekt wirtschaftlich läuft",
        text: "Ob ein Auftrag Geld bringt, sehen Sie erst nach der Schlussrechnung. Da ist es für Gegensteuern zu spät.",
      },
      {
        title: "Rechtliche Anforderungen sind unklar erfüllt",
        text: "Arbeitszeiten müssen dokumentiert werden. Mit Zetteln und Excel ist das im Ernstfall schwer nachweisbar – und bei einer Prüfung unangenehm.",
      },
    ],
    solutions: [
      {
        title: "Zwei Taps, fertig",
        text: "App öffnen, Projekt antippen, Start. Am Ende Stopp. Wer will, erfasst zusätzlich Tätigkeit und Material – aber die Grundlage steht mit zwei Berührungen.",
      },
      {
        title: "Funktioniert auch ohne Netz",
        text: "Im Keller, im Tunnel, auf dem Land: Eingaben werden lokal gespeichert und übertragen, sobald wieder Empfang da ist. Nichts geht verloren.",
      },
      {
        title: "Auswertung, solange es noch zählt",
        text: "Sie sehen laufend, wie viele Stunden auf welchem Projekt liegen und wie das zur Kalkulation passt – nicht erst bei der Schlussrechnung.",
      },
    ],
    sections: [
      {
        heading: "Warum die Erfassung sofort passieren muss",
        body:
          "Jede Zeiterfassung, die auf Erinnerung beruht, ist unvollständig. Wer am Freitagabend rekonstruiert, was er am Dienstag gemacht hat, rundet ab, vergisst Fahrtzeiten und übersieht kurze Zusatzarbeiten. Studien und Praxiserfahrung zeigen dasselbe Bild: Nachträglich erfasste Zeit liegt systematisch unter der tatsächlich geleisteten. Für Sie heißt das direkt entgangener Umsatz bei jedem Projekt, das nach Aufwand abgerechnet wird. Der einzige wirksame Hebel dagegen ist, die Erfassung so einfach zu machen, dass sie im Moment passiert statt später. Deshalb bauen wir sie auf zwei Berührungen aus – jedes zusätzliche Pflichtfeld senkt die Erfassungsquote messbar.",
      },
      {
        heading: "Rechtlicher Rahmen in Österreich",
        body:
          "Arbeitgeber sind nach dem Arbeitszeitgesetz verpflichtet, Arbeitszeitaufzeichnungen zu führen. Der Europäische Gerichtshof hat die Anforderungen an ein objektives, verlässliches und zugängliches System zusätzlich betont. Praktisch heißt das: Die Aufzeichnung muss nachvollziehbar sein, sie muss die tatsächliche Arbeitszeit abbilden, und sie muss im Bedarfsfall vorgelegt werden können. Eine digitale Erfassung mit Zeitstempel, Änderungshistorie und Export erfüllt diese Anforderungen deutlich besser als Papier oder eine Excel-Liste, die sich nachträglich spurlos ändern lässt. Wichtige Einschränkung: Wir liefern die technische Umsetzung – die arbeitsrechtliche Beurteilung für Ihren Betrieb, insbesondere zu Betriebsvereinbarungen und Mitbestimmung, gehört zu Ihrem Arbeitsrechtsberater.",
      },
      {
        heading: "Projektbezogen erfassen lohnt sich doppelt",
        body:
          "Viele Betriebe erfassen nur, wer wie lange gearbeitet hat – das reicht für die Lohnverrechnung, aber sonst für nichts. Wird zusätzlich erfasst, auf welchem Projekt die Zeit angefallen ist, ändert sich die Aussagekraft grundlegend. Sie sehen dann während der Laufzeit, ob ein Auftrag im Rahmen bleibt, welche Art von Arbeit systematisch länger dauert als kalkuliert und welche Kunden mehr Aufwand verursachen, als ihr Umsatz rechtfertigt. Diese Auswertung ist für die Kalkulation künftiger Angebote oft wertvoller als der eigentliche Zweck der Erfassung – und sie kostet fast nichts extra, weil nur ein zusätzliches Feld beim Start ausgewählt wird.",
      },
    ],
    visual: {
      kind: "compare",
      caption: "Zettel, Tabelle oder mobile Erfassung",
      columns: ["Zettel & Tabelle", "Mobile Erfassung"],
      rows: [
        { criterion: "Zeiten stehen am selben Tag im System", a: "no", b: "yes" },
        { criterion: "Zuordnung zu Auftrag und Kunde ohne Nachfragen", a: "no", b: "yes" },
        { criterion: "Grundlage für die Abrechnung ohne Abtippen", a: "no", b: "yes" },
        { criterion: "Nachträgliche Änderung bleibt nachvollziehbar", a: "no", b: "yes" },
        { criterion: "Funktioniert ohne Empfang", a: "yes", b: "part" },
        { criterion: "Kein Gerät nötig", a: "yes", b: "no" },
      ],
      note: "„Teilweise“ bei fehlendem Empfang: Die Erfassung läuft offline weiter und überträgt, sobald wieder Verbindung besteht.",
    },
    deliverables: [
      "Mobile Erfassung in zwei Taps, offline nutzbar",
      "Projekt- & tätigkeitsbezogene Zuordnung",
      "Automatische Berechnung von Zuschlägen & Pausen",
      "Freigabe-Ablauf für Vorgesetzte",
      "Export zur Lohnverrechnung & Auswertung je Projekt",
    ],
    example: {
      title: "Die Stunden, die vorher niemand aufgeschrieben hat",
      persona: "Typisches Szenario: Montagebetrieb mit 20 Mitarbeitern im Außendienst",
      problem:
        "Stundenzettel wurden handschriftlich geführt und am Monatsende abgegeben – oft unvollständig, teilweise unleserlich. Fahrtzeiten und kurze Zusatzarbeiten fehlten regelmäßig.",
      solution:
        "Eine mobile Erfassung mit Projektauswahl und Offline-Betrieb, automatischer Zuschlagsberechnung, Freigabe durch den Vorarbeiter und Export in die Lohnverrechnung.",
      result:
        "Zeiten werden im Moment erfasst statt rekonstruiert. Verrechenbare Stunden, die früher untergingen, sind erfasst – und die Monatsabrechnung dauert Stunden statt Tage.",
    },
    faq: [
      {
        q: "Empfinden Mitarbeiter das nicht als Überwachung?",
        a: "Das hängt stark davon ab, was erfasst wird. Wir erfassen Arbeitszeit auf Projekten – keine durchgehende Standortverfolgung. Das ist ein wichtiger Unterschied, den Sie im Team auch so kommunizieren sollten. Wo ein Betriebsrat existiert, gehört das Thema ohnehin vorab besprochen und meist in eine Betriebsvereinbarung.",
      },
      {
        q: "Können wir Zuschläge und Pausenregeln hinterlegen?",
        a: "Ja. Überstunden, Nacht- und Feiertagszuschläge, automatischer Pausenabzug, unterschiedliche Regeln je Kollektivvertrag oder Mitarbeitergruppe – das ist Standard. Ihre konkreten Regeln nehmen wir zu Projektbeginn auf.",
      },
      {
        q: "Geht das auch mit Terminal statt Handy?",
        a: "Ja. Für Standorte mit festem Arbeitsplatz ist ein Terminal mit Chip oder Karte oft praktischer, gerade wenn private Handys nicht genutzt werden sollen. Beide Wege lassen sich parallel betreiben.",
      },
    ],
    relatedServices: ["softwareentwicklung", "it-consulting"],
    relatedSlugs: ["handwerk", "transport-logistik", "mitarbeiter-app"],
  },

  // ────────────────────────────────────────────────────────────────
  {
    slug: "dokumenten-automatisierung",
    icon: "file-text",
    title: "Dokumenten-Automatisierung",
    navTitle: "Dokumenten-Automatisierung",
    tagline: "Rechnungen, Lieferscheine und Formulare lesen sich selbst aus.",
    summary:
      "KI-gestützte Erkennung von Rechnungen, Belegen und Formularen: Daten werden ausgelesen, geprüft und übergeben – statt von Hand abgetippt.",
    keywords: [
      "Rechnungen automatisch auslesen",
      "Dokumentenerkennung Software",
      "Belegerfassung automatisieren",
      "OCR Rechnungsverarbeitung Österreich",
      "Dokumentenautomatisierung KMU",
    ],
    metaTitle: "Belege automatisch erfassen lassen",
    metaDescription:
      "Rechnungen und Lieferscheine lesen sich selbst aus, geprüft wird nur was abweicht. Jetzt kostenloses Erstgespräch sichern.",
    heroEyebrow: "Lösung",
    heroHeadline: "Niemand sollte Zahlen von einem Blatt in ein Feld abtippen",
    heroSub:
      "Rechnungen, Lieferscheine, Formulare: Jeden Tag überträgt jemand dieselben Angaben von einem Dokument in ein System. Das ist stumpfe Arbeit, fehleranfällig – und seit einiger Zeit gut automatisierbar.",
    pains: [
      {
        title: "Abtippen bindet qualifizierte Leute",
        text: "Eingangsrechnungen erfassen, Lieferscheine abgleichen, Formulare übertragen. Das machen Menschen, die deutlich Wertvolleres könnten.",
      },
      {
        title: "Tippfehler fallen erst spät auf",
        text: "Eine verrutschte Ziffer im Betrag oder ein falsches Datum bemerkt niemand sofort. Der Fehler taucht Wochen später beim Abgleich auf – und die Suche dauert länger als die Erfassung gedauert hätte.",
      },
      {
        title: "Dokumente kommen in allen Formaten",
        text: "PDF per E-Mail, Papier in der Post, Foto per WhatsApp, Scan aus dem Kopierer. Jeder Weg braucht eine andere Behandlung.",
      },
      {
        title: "Nichts ist auffindbar",
        text: "Die Rechnung von letztem März? Irgendwo im Ordner, im Postfach oder im Archiv. Suchen kostet mehr Zeit als jede einzelne Erfassung.",
      },
    ],
    solutions: [
      {
        title: "Auslesen statt abtippen",
        text: "Lieferant, Rechnungsnummer, Datum, Beträge, Steuersätze und Positionen werden automatisch erkannt – auch bei Layouts, die das System noch nie gesehen hat.",
      },
      {
        title: "Prüfen statt blind übernehmen",
        text: "Erkannte Werte werden gegen Bestellung oder Stammdaten geprüft. Was eindeutig passt, läuft durch. Was abweicht, landet zur Kontrolle bei einem Menschen.",
      },
      {
        title: "Volltextsuche über alles",
        text: "Jedes Dokument wird durchsuchbar abgelegt. Suche nach Lieferant, Betrag, Zeitraum oder einem Wort im Text – Treffer in Sekunden statt Ordnersuche.",
      },
    ],
    sections: [
      {
        heading: "Was moderne Dokumentenerkennung wirklich kann",
        body:
          "Die Texterkennung früherer Generationen war auf feste Vorlagen angewiesen: Für jeden Lieferanten musste hinterlegt werden, an welcher Stelle welches Feld steht. Bei einem neuen Layout begann die Einrichtung von vorn – der Grund, warum viele Betriebe solche Systeme nach kurzer Zeit wieder aufgaben. Aktuelle Verfahren arbeiten inhaltlich statt positionsbasiert: Sie erkennen, dass eine Zahl ein Rechnungsbetrag ist, weil sie im Kontext an der richtigen Stelle steht – unabhängig davon, wo auf dem Blatt sie gedruckt ist. Damit funktionieren auch schiefe Handyfotos, unbekannte Lieferanten und fremdsprachige Belege ohne Voreinrichtung. Das ist der Unterschied, der die Technologie für kleinere Betriebe erst brauchbar gemacht hat.",
      },
      {
        heading: "Warum die Prüfschleife bleiben muss",
        body:
          "Keine Erkennung ist fehlerfrei, und Automatisierung ohne Kontrolle ist in der Buchhaltung ein schlechtes Geschäft: Ein automatisch verbuchter falscher Betrag kostet mehr an Suche und Korrektur, als die manuelle Erfassung gekostet hätte. Deshalb bauen wir immer eine Schwelle ein. Das System bewertet seine eigene Sicherheit: Ist ein Dokument eindeutig erkannt und stimmt es mit Bestellung oder Stammdaten überein, läuft es durch. Bei Abweichung, unbekanntem Lieferanten oder unsicherer Erkennung geht es zur Prüfung an einen Menschen – mit hervorgehobenen unsicheren Stellen, sodass die Kontrolle Sekunden statt Minuten dauert. Diese Schwelle stellen Sie selbst ein und können sie mit wachsendem Vertrauen anpassen.",
      },
      {
        heading: "Der unterschätzte Nebeneffekt: Auffindbarkeit",
        body:
          "Die Zeitersparnis beim Erfassen ist der Grund, aus dem solche Projekte meist gestartet werden. Der Effekt, den Kunden im Nachhinein am häufigsten hervorheben, ist ein anderer: Alle Dokumente sind plötzlich durchsuchbar. Wer je eine bestimmte Rechnung aus dem Vorjahr in Papierordnern oder verstreuten PDF-Ordnern gesucht hat, kennt den Aufwand. Wenn stattdessen jedes Dokument mit ausgelesenen Feldern und Volltext abgelegt ist, dauert dieselbe Suche Sekunden – nach Lieferant, Zeitraum, Betrag oder einem beliebigen Wort. Bei Prüfungen, Reklamationen und Gewährleistungsfällen ist das oft mehr wert als die eingesparte Erfassungszeit.",
      },
    ],
    visual: {
      kind: "matrix",
      caption: "Welche Belege sich automatisch auslesen lassen",
      good: {
        title: "Gut automatisierbar",
        items: [
          "Eingangsrechnungen in gleichbleibendem Aufbau",
          "Lieferscheine mit Positionsliste",
          "Formulare mit festen Feldern",
          "Digitale PDF mit auswählbarem Text",
          "Belege, die regelmäßig vom selben Partner kommen",
        ],
      },
      bad: {
        title: "Bleibt Handarbeit",
        items: [
          "Handschriftliche Notizen auf dem Beleg",
          "Schlechte Fotos und schiefe Scans",
          "Einzelstücke ohne wiederkehrendes Muster",
          "Dokumente, bei denen jemand fachlich entscheiden muss",
        ],
      },
      note: "Entscheidend ist nicht die Technik, sondern die Wiederholung: Was hundertmal im gleichen Aufbau kommt, rechnet sich. Das Einzelstück nicht.",
    },
    deliverables: [
      "Automatische Erkennung von Rechnungen, Lieferscheinen & Formularen",
      "Eingang über E-Mail, Scan, Foto oder Upload",
      "Abgleich gegen Bestellungen & Stammdaten",
      "Prüfschleife mit hervorgehobenen Unsicherheiten",
      "Durchsuchbares Archiv & Übergabe an Ihre Buchhaltung",
    ],
    example: {
      title: "Vom Postkorb in die Buchhaltung, ohne Zwischenstopp",
      persona: "Typisches Szenario: Handelsbetrieb mit hohem Belegaufkommen",
      problem:
        "Eingangsrechnungen kamen als PDF, Papier und Foto. Zwei Personen erfassten sie von Hand, glichen sie mit Bestellungen ab und legten sie ab. Die Suche nach älteren Belegen dauerte regelmäßig lange.",
      solution:
        "Automatische Erkennung aller Eingangsdokumente mit Abgleich gegen Bestellungen, Prüfschleife bei Abweichungen und durchsuchbarem Archiv.",
      result:
        "Der Großteil der Belege läuft ohne manuelle Erfassung durch. Geprüft wird nur noch, was abweicht – und ältere Belege sind in Sekunden auffindbar.",
    },
    faq: [
      {
        q: "Funktioniert das auch bei handschriftlichen Belegen?",
        a: "Teilweise. Gedruckte Dokumente werden sehr zuverlässig erkannt, Handschrift deutlich schlechter – gerade bei Zahlen. Für handschriftliche Belege planen wir daher grundsätzlich die manuelle Prüfung ein. Wir sagen Ihnen vorher ehrlich, welcher Anteil Ihrer Dokumente realistisch automatisierbar ist.",
      },
      {
        q: "Können die Daten in unser Buchhaltungsprogramm übernommen werden?",
        a: "In den meisten Fällen ja, über Schnittstelle oder Importformat. Welche Wege Ihr Programm unterstützt, prüfen wir vorab konkret – das ist der Punkt, an dem sich entscheidet, ob die Automatisierung tatsächlich durchgängig funktioniert oder nur bis zur letzten Meile.",
      },
      {
        q: "Wo werden die Dokumente verarbeitet?",
        a: "Das legen wir gemeinsam fest. Bei sensiblen Unterlagen setzen wir auf Verarbeitung innerhalb der EU oder auf Modelle, die in Ihrer eigenen Infrastruktur laufen. Bei unkritischen Dokumenten sind auch andere Wege wirtschaftlich sinnvoll – die Entscheidung treffen wir vor der Umsetzung, nicht danach.",
      },
    ],
    relatedServices: ["kuenstliche-intelligenz", "softwareentwicklung", "it-consulting"],
    relatedSlugs: ["steuerberater-kanzleien", "kundenportal", "excel-abloesen"],
  },
];

export function getSolution(slug: string) {
  return solutions.find((s) => s.slug === slug);
}
